import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Share2, Copy, Check, Link2Off, Loader2, Users } from "lucide-react";

export default function ShareTripDialog({ trip }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(trip.share_enabled ? trip.share_token : "");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const link = token ? `${window.location.origin}/share/${token}` : "";

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/me/itineraries/${trip.id}/share`);
      setToken(data.share_token);
      toast.success("Share link ready");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      await api.delete(`/me/itineraries/${trip.id}/share`);
      setToken("");
      toast.success("Sharing turned off");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="share-trip-btn"
          variant="outline"
          className="rounded-full border-primary/40 text-primary bg-black/30 backdrop-blur hover:bg-primary/10"
        >
          <Share2 className="w-4 h-4 mr-2" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light tracking-tighter">Share with family</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <p className="text-sm text-muted-foreground flex items-start gap-2">
            <Users className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            Anyone with this link can view your itinerary — countdown, day-by-day plan and notes. Your balance and documents stay private.
          </p>

          {token ? (
            <>
              <div className="flex gap-2">
                <Input value={link} readOnly data-testid="share-link-input" className="bg-input border-border h-11 text-sm" />
                <Button onClick={copy} data-testid="copy-link-btn" className="rounded-md bg-primary text-primary-foreground shrink-0 h-11">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <Button
                variant="ghost"
                onClick={disable}
                disabled={loading}
                data-testid="disable-share-btn"
                className="text-muted-foreground hover:text-destructive-foreground"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2Off className="w-4 h-4 mr-2" />}
                Turn off sharing
              </Button>
            </>
          ) : (
            <Button
              onClick={generate}
              disabled={loading}
              data-testid="generate-share-btn"
              className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform gold-glow"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create private link"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
